import { useState, useEffect, useRef } from 'react';
import api from '../config/axios';
import { formatErrorMessage } from '../utils/apiUtils';

const lookupCache = new Map();

// Helper function to determine additional fields needed for FK resolution
function getAdditionalFieldsForTable(tableName) {
  const tableFieldMappings = {
    'SKE_Tabelle': ',TAB_Id_Moduli',
    'SKE_Campi': ',CAM_Id_Tabelle,CAM_Id_TipiCampi,CAM_Extra_Tabelle',
    'SKE_Oggetti': ',OGG_Id_Tabelle,OGG_Id_Moduli', // ✅ FIX: OGG_Id_Tabella → OGG_Id_Tabelle
    'SKE_Attributi': ',ATT_Id_Oggetti,ATT_TipiCampi,ATT_Super_Oggetti',
    'SKE_Funzioni': ',FUN_Id_Moduli',
    // Add other tables as needed
  };
  
  return tableFieldMappings[tableName] || '';
}

// Helper function to determine if foreign key info should be shown
function shouldShowForeignKeyInfo(field, tableName) {
  // Show foreign key info for fields ending with "Id_Tabelle"
  if (field.endsWith('Id_Tabelle')) {
    return true;
  }
  
  // Show foreign key info for table lookups (SKE_Tabelle)
  if (tableName === 'SKE_Tabelle') {
    return true;
  }
  
  // Add other specific cases if needed
  if (field.includes('tabelle') || field.includes('Tabelle')) {
    return true;
  }
  
  return false;
}

// Helper function to fetch nested lookup data for complex lookups
async function fetchNestedLookup(baseData, nestedConfig, logger, apiEndpoint) {
  try {
    const { foreignKeyField, nestedTableName, nestedValueField, nestedLabelField } = nestedConfig;
    
    // Extract unique foreign key values from base data
    const foreignKeyValues = [...new Set(
      baseData
        .map(item => item[foreignKeyField])
        .filter(val => val != null && val !== '')
    )];

    if (foreignKeyValues.length === 0) {
      return {};
    }

    // Fetch nested lookup data using the same GET format as main lookups
    const params = {
      limit: 10000,
      offset: 0,
      fields: `${nestedValueField},${nestedLabelField}`,
      filters: JSON.stringify({}),
      orderBy: nestedLabelField,
      orderDirection: 'ASC',
      lookupMode: false,
    };

    const response = await api.get(`${apiEndpoint}/${nestedTableName}`, { params });
    const records = response.data?.records || response.data || [];

    // Create a map of nested data keyed by nestedValueField
    const nestedMap = {};
    records.forEach(record => {
      const key = record[nestedValueField];
      if (key != null) {
        nestedMap[key] = record[nestedLabelField];
      }
    });

    console.log(`[fetchNestedLookup] Fetched ${records.length} records from ${nestedTableName}`, nestedMap);
    return nestedMap;
  } catch (error) {
    console.error('[fetchNestedLookup] Error fetching nested lookup:', error);
    return {};
  }
}

const useLookupOptions = (lookupConfig, apiEndpoint = '/crud') => {
  const [lookupOptions, setLookupOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const configRef = useRef(JSON.stringify(lookupConfig));

  useEffect(() => {
    const currentConfigStr = JSON.stringify(lookupConfig);
    if (currentConfigStr === configRef.current && Object.keys(lookupOptions).length > 0) {
      return;
    }
    configRef.current = currentConfigStr;

    if (!lookupConfig || typeof lookupConfig !== 'object' || Object.keys(lookupConfig).length === 0) {
      setLookupOptions({});
      setLoading(false);
      setError(null);
      return;
    }

    // ✅ DEBUG: Clear cache when config changes to ensure fresh data
    console.log('[useLookupOptions] Clearing lookup cache due to config change');
    lookupCache.clear();

    const fetchLookups = async () => {
      setLoading(true);
      setError(null);
      const newLookupOptions = {};
      let fetchError = null;

      const lookupPromises = Object.entries(lookupConfig).map(async ([field, config]) => {
        // **FIX**: Gestione staticOptions per lookup semplici senza database
        if (config.staticOptions) {
          newLookupOptions[field] = config.staticOptions;
          return;
        }

        const {
          tableName,
          valueField,
          labelField,
          additionalFields = [],
          filters = {},
          orderBy,
          orderDirection = 'ASC',
          limit = 10000000,
          nestedLookup, // ✅ NEW: nested lookup configuration
        } = config;

        if (!tableName || !valueField || !labelField) {
          console.warn(`[useLookupOptions] Invalid config for field '${field}'. Missing tableName, valueField, or labelField.`);
          return;
        }

        const cacheKey = `${tableName}-${valueField}-${labelField}-${JSON.stringify(filters)}-${orderBy}-${orderDirection}-${JSON.stringify(nestedLookup || {})}`;

        if (lookupCache.has(cacheKey)) {

          newLookupOptions[field] = lookupCache.get(cacheKey);
          return;
        }


        const params = {
          limit,
          offset: 0,
          // Include foreign key fields for proper resolution and display
          fields: `${valueField},${labelField}${getAdditionalFieldsForTable(tableName)}${additionalFields.length > 0 ? `,${additionalFields.join(',')}` : ''}${nestedLookup ? `,${nestedLookup.foreignKeyField}` : ''}`,
          filters: JSON.stringify(filters),
          ...(orderBy && { orderBy }),
          ...(orderDirection && { orderDirection }),
          lookupMode: false, // Ensure FK resolution happens
        };

        try {
          const response = await api.get(`${apiEndpoint}/${tableName}`, { params });
          const records = response.data?.records || response.data || [];

          // ✅ NEW: Fetch nested lookup data if configured
          let nestedDataMap = {};
          if (nestedLookup) {
            console.log(`[useLookupOptions] Fetching nested lookup for field '${field}'`, nestedLookup);
            nestedDataMap = await fetchNestedLookup(records, nestedLookup, console, apiEndpoint);
            console.log(`[useLookupOptions] Nested data map for '${field}':`, nestedDataMap);
          }

          const formattedOptions = records.map(record => {
            let label = record[labelField] || '';
            
            // ✅ NEW: Handle nested lookup formatting
            if (nestedLookup && nestedDataMap) {
              const foreignKeyValue = record[nestedLookup.foreignKeyField];
              const nestedLabel = nestedDataMap[foreignKeyValue];
              console.log(`[useLookupOptions] Record: ${record[labelField]}, FK value: ${foreignKeyValue}, Nested label: ${nestedLabel}`);
              if (nestedLabel) {
                label = nestedLookup.formatLabel 
                  ? nestedLookup.formatLabel(label, nestedLabel)
                  : `${label} (${nestedLabel})`;
              }
            }
            // MODIFIED: Check if foreign key info should be shown based on field name and table
            else if (shouldShowForeignKeyInfo(field, tableName)) {
              const foreignKeyTexts = [];
              Object.keys(record).forEach(key => {
                if (key.endsWith('_text') && record[key] && record[key].trim() !== '') {
                  foreignKeyTexts.push(record[key]);
                }
              });
              
              if (foreignKeyTexts.length > 0) {
                label = `${label} (${foreignKeyTexts.join(', ')})`;
              }
            }

            return {
              value: record[valueField],
              label: label,
              original: record,
            };
          });

          lookupCache.set(cacheKey, formattedOptions);
          newLookupOptions[field] = formattedOptions;

        } catch (err) {
          console.error(`[useLookupOptions] Error fetching lookup for ${field} (${tableName}):`, err);
          fetchError = formatErrorMessage(err);
          newLookupOptions[field] = [];
        }
      });

      await Promise.all(lookupPromises);

      setLookupOptions(newLookupOptions);
      setError(fetchError);
      setLoading(false);
    };

    fetchLookups();
  }, [JSON.stringify(lookupConfig), apiEndpoint]);

  return { lookupOptions, loading, error };
};

export default useLookupOptions;